import { ref, computed, toValue, watch, onUnmounted } from 'vue';
import { useForecastTableModelStorage } from '@/composables/useForecastTableModelStorage';

const BLEND_MODEL_ID = -1;

// Model ids must be numbers to match the API's numeric model_id (favorites and
// legacy storage can supply them as strings, which breaks selection matching).
const toNumericId = (value) => {
  if (value == null || value === '') return null;
  const id = Number(value);
  return Number.isNaN(id) ? null : id;
};

// Orchestrates single-select model selection for the Forecast Table: default
// selection, per-spot persistence, and the EventBus contract the legacy FxTable
// listens on. The single-select analogue of useWindGraphModelSelection — the
// presentational ModelSelector stays a leaf; this owns the policy around it.
//
//   spotId:      getter/ref for the resolved spot id (persistence + event scoping)
//   containerId: getter/ref for the table's container id (event scoping)
//   initialSelection: getter/ref for a preset model id (favorites); else null
export const useForecastTableModelSelection = ({ spotId, containerId, initialSelection = null }) => {
  const storage = useForecastTableModelStorage();

  const resolvedSpotId = computed(() => toValue(spotId));
  const resolvedContainerId = computed(() => toValue(containerId));

  const selectedModelId = ref(null);
  // The model list reported by ModelSelector, used to compute the default.
  const availableModels = ref([]);
  // A saved/user choice suppresses the recomputed default.
  let selectionIsUserChosen = false;

  function isAccessible(model) {
    return !model.upgrade_available;
  }

  // Default when nothing is saved: free users get the Blend (-1) model, others
  // the first accessible model. Mirrors the wind graph and the model rule the
  // legacy FxTable selector used.
  function defaultModelId(models) {
    if (!models.length) return null;

    const hasPremiumAccess = models.some((model) => model.is_premium && isAccessible(model));
    const blend = models.find((model) => model.model_id === BLEND_MODEL_ID);
    const firstAccessible = models.find(isAccessible);
    const primary = hasPremiumAccess ? firstAccessible : (blend || firstAccessible);

    return primary ? primary.model_id : null;
  }

  // Whether an event is addressed to our table instance. Prefer containerId
  // (favorites can duplicate a spot), else fall back to spotId — mirroring
  // ForecastTable's cosmos-forecast-table-rendered filter.
  function isForThisInstance(detail) {
    if (resolvedContainerId.value) return detail.containerId == resolvedContainerId.value;
    return detail.spotId == resolvedSpotId.value;
  }

  // Notify the legacy FxTable, which re-renders for the given model.
  function broadcast() {
    window.$EventBus.dispatchEvent('jw-forecast-table-model-selected', {
      spotId: resolvedSpotId.value,
      containerId: resolvedContainerId.value,
      selectedModelId: selectedModelId.value,
    });
  }

  // Set the selection and notify the table. Only deliberate user choices are
  // persisted; defaults are recomputed each load so they stay current.
  function setSelection(id, { userChosen }) {
    const modelId = toNumericId(id);
    if (modelId == null) return;

    selectionIsUserChosen = userChosen;
    selectedModelId.value = modelId;

    if (userChosen) storage.save(resolvedSpotId.value, modelId);

    broadcast();
  }

  function onModelsLoaded(models) {
    availableModels.value = models || [];
    if (selectionIsUserChosen) return;

    // Restore a preset (favorites) or saved choice if it's still available,
    // else apply the default.
    const preset = toNumericId(toValue(initialSelection));
    const seed = preset != null ? preset : storage.read(resolvedSpotId.value);
    const seedAvailable = seed != null && availableModels.value.some((m) => m.model_id === seed);

    if (seedAvailable) {
      setSelection(seed, { userChosen: true });
      return;
    }

    setSelection(defaultModelId(availableModels.value), { userChosen: false });
  }

  function onModelSelected(id) {
    setSelection(id, { userChosen: true });
  }

  // The table's own data fetch can finish after the model list's, so FxTable
  // may register its listener after our initial broadcast and miss it. It asks
  // us to re-broadcast on init; reply with the current selection (once we have
  // one) so it renders regardless of which fetch won the race.
  function handleModelRequested(e) {
    if (!isForThisInstance(e.detail || {})) return;
    if (selectedModelId.value != null) broadcast();
  }

  window.$EventBus.addEventListener('jw-forecast-table-request-model', handleModelRequested);

  // The Forecast Table updates in place across spot changes (rather than
  // remounting), so reset selection state when the spot changes; the fresh
  // model list then restores that spot's saved choice or falls back to default.
  watch(resolvedSpotId, () => {
    selectionIsUserChosen = false;
    selectedModelId.value = null;
    availableModels.value = [];
  });

  onUnmounted(() => {
    window.$EventBus.removeEventListener('jw-forecast-table-request-model', handleModelRequested);
  });

  return { selectedModelId, onModelsLoaded, onModelSelected };
};
